<template>
  <div>
    <Loader v-if="isDataSlaLoading"/>
    <Sla
      v-else
      :sla="sla"
      :agility="hasAgilityAccess"
      :backup="hasBackupAccess"
      :director="hasDirectorAccess"
    />
    <div v-if="hasAgilityAccess && viewingAgilityPages">
      <Loader v-if="isDataVsphereLoading"/>
      <Agility
        v-else
        :replicated="vsphere.protected"
        :vms="vsphere.vms"
        :cpu="vsphere.cpu"
        :memory="vsphere.memory"
        :storage="vsphere.storage"
        :problems="problems"
      />
    </div>
    <div v-if="hasDirectorAccess && viewingDirectorPages">
      <Loader v-if="isDataVcloudLoading"/>
      <Director
        v-else-if="directors"
        :pools="directors.pools"
        :is-dashboard="true"
        :only-global="true" />
    </div>

    <div class="row">
      <!-- BACKUP -->
      <div class="col-tab-6" v-if="hasBackupAccess">
        <Loader v-if="isDataBackupLoading"/>
        <section v-else class="section">
          <HeaderSection :title="$t('views.dashboard.backup.title')" />
          <div class="section-content">
            <Label
            :qty="backup.registered_devices"
            :label="$t('views.dashboard.backup.devices')"
            :tooltipText="c('backup')"/>
          </div>
          <div class="section-content is-justify-start is-full">
            <ProgressStandalone
            :value="backup.total_gb_protected.value"
            :label="$t('views.dashboard.backup.data')"
            :unit="backup.total_gb_protected.unit"/>
            <ProgressStandalone
            total="100"
            :value="Math.round((backup.success_rate + Number.EPSILON) * 100) / 100"
            :label="$t('views.dashboard.backup.rate')"
            unit="%"
            color="beta"/>
          </div>
        </section>
      </div>
      <!-- SUPPORT -->
      <div class="col-tab-6">
        <Loader v-if="isDataTicketLoading"/>
        <section class="section" v-else>
          <HeaderSection :title="$t('views.dashboard.support.title')">
          </HeaderSection>
          <div class="section-content">
            <div class="pv-10"></div>
          </div>
          <div class="section-content is-justify-start is-full">
            <ProgressStandalone
            :value="ticketsCount"
            :label="$t('views.dashboard.support.tickets')"
            :unit="$t('views.dashboard.support.ticketsUnit')"/>
          </div>
        </section>
      </div>

    </div>
  </div>

</template>

<script>
import { mapGetters } from 'vuex';
import { eventHubMixin } from '@/mixins/event';


import VSPHERE from '@/api/vsphere';
import DIRECTOR from '@/api/director';
import SUPPORT from '@/api/support';
import PROBLEMS from '@/api/problems';
import AVAMAR from '@/api/avamar';

import HeaderSection from '@/components/HeaderSection.vue';
import Label from '@/components/Label.vue';
import ProgressStandalone from '@/components/ProgressStandalone.vue';
import Loader from '@/components/Loader.vue';
import ProgressGroupDirector from '@/components/ProgressGroupDirector.vue';
import ProgressGroupAgility from '@/components/ProgressGroupAgility.vue';
import ProgressGroupSla from '@/components/ProgressGroupSla.vue';


export default {
  name: 'dashboard',
  mixins: [eventHubMixin],
  components: {
    Sla: ProgressGroupSla,
    Director: ProgressGroupDirector,
    Agility: ProgressGroupAgility,
    Loader,
    ProgressStandalone,
    Label,
    HeaderSection,
  },
  data: () => ({
    ticketsCount: null,
    vsphere: {},
    problems: null,
    directors: null,
    isDataVsphereLoading: true,
    isDataVcloudLoading: true,
    isDataSlaLoading: true,
    isDataBackupLoading: true,
    isDataTicketLoading: true,
    backup: {
      registered_devices: 0,
      total_gb_protected: 0,
      success_rate: 0,
    },
    sla: {
      sla_otrs: null,
      sla_agility: null,
      sla_backup: null,
      sla_director: null,
    },
  }),
  computed: {
    ...mapGetters({
      hasAgilityAccess: 'auth/hasAgilityAccess',
      hasBackupAccess: 'auth/hasBackupAccess',
      hasDirectorAccess: 'auth/hasDirectorAccess',
      viewingAgilityPages: 'auth/viewingAgilityPages',
      viewingDirectorPages: 'auth/viewingDirectorPages'
    }),
  },
  created() {
    this.initVsphere();
    this.getProblems();
    this.get_sla();
    this.getTicketsCount();

    if (this.hasDirectorAccess && this.viewingDirectorPages) {
      this.getDirector();
    }

    if (this.hasAgilityAccess && this.viewingAgilityPages) {
      this.getVsphereTotal();
    }

    if (this.hasBackupAccess) {
      this.get_backup();
    }
  },
  mounted() {
    if (!this.$store.state.auth.user.cgu_accepted) {
      setTimeout(() => {
        this.emitOpenModal('modal-cgu');
      }, 0);
    }
  },
  methods: {
    initVsphere() {
      this.vsphere = {
        protected: 0,
        vms: {
          running: 0,
          total: 0,
        },
        cpu: {
          running: 0,
          total: 0,
        },
        memory: {
          running: 0,
          total: 0,
        },
        storage: {
          running: 0,
          total: 0,
        },
      };
    },

    getProblems() {
      PROBLEMS.list().then((res) => {
        if (res && res.data) {
          this.problems = res.data.length;
        }
      });
    },

    getDirector() {
      DIRECTOR.list().then(
        (res) => {
          if (res && res.data.length) {
            const result = res.data.reduce((a, b) => {
              a.pools = [...a.pools, ...b.pools]
              return a;
            })
            this.directors = result
          }
          this.isDataVcloudLoading = false;
        },
      );
    },

    getVsphereTotal() {
      VSPHERE.total().then(
        (res) => {
          if (res && res.data.length) {
            const result = res.data.reduce((a, b) => {
              a.cpu.total += b.cpu.total
              a.cpu.running += b.cpu.running
              a.memory.total += b.memory.total
              a.memory.running += b.memory.running
              a.protected += b.protected
              a.storage.running += b.storage.running
              a.storage.total += b.storage.total
              a.vms.running += b.vms.running
              a.vms.total += b.vms.total
              return a;
            })
            this.vsphere = result
          }
          this.isDataVsphereLoading = false;
        },
      );
    },

    getTicketsCount() {
      SUPPORT.list('state-type=open').then(
        (res) => {
          if (res && res.data && res.data.tickets) {
            this.ticketsCount = res.data.tickets.filter(elem => elem.state === 'open').length;
          } else {
            this.ticketsCount = 0;
          }
          this.isDataTicketLoading = false;
        },
      );
    },

    get_sla() {
      PROBLEMS.listServices().then(
        (res) => {
          if (res && res.data) {
            this.sla = res.data;
          }
          this.isDataSlaLoading = false;
        },
      );
    },

    get_backup() {
      AVAMAR.total().then(
        (res) => {
          if (res && res.data) {
            this.backup = res.data;
          }
          this.isDataBackupLoading = false;
        },
      );
    },

  },
};
</script>
